import { Icon } from '@repo/foundations';
import {
	IconBook,
	IconChartBar,
	IconChartHistogram,
	IconCircleCaretRight,
	IconClick,
	IconClipboardCheck,
	IconCloud,
	IconColumns,
	IconDatabase,
	IconFileDescription,
	IconFolder,
	IconFolders,
	IconLayoutDashboard,
	IconQuestionMark,
	IconSchema,
	IconTable,
	IconTemplate,
	IconUserCircle,
} from '@tabler/icons-react';
import { capitalize } from 'lodash-es';
import { DEFAULT_ICONS } from '../constants';
import { EntityType } from '../enums/entityType';

export interface IEntityTypeDisplayInfo {
	renderIcon: (size: number) => JSX.Element;
	label: string;
}

export const getEntityDisplayType = (
	entityType: EntityType | undefined
): string => capitalize(entityType?.replace('_', ' '));

export const getEntityTypeDisplayInfo = (
	entityType: EntityType | undefined
): IEntityTypeDisplayInfo => {
	switch (entityType) {
		case EntityType.user:
			return {
				renderIcon: (size: number) => <IconUserCircle size={size} />,
				label: 'People',
			};
		case EntityType.dictionary_term:
			return {
				renderIcon: (size: number) => <IconBook size={size} />,
				label: 'Terms',
			};
		case EntityType.glossary:
			return {
				renderIcon: (size: number) => <IconBook size={size} />,
				label: 'Glossary',
			};
		case EntityType.metric:
			return {
				renderIcon: (size: number) => <IconChartHistogram size={size} />,
				label: 'Metrics',
			};
		case EntityType.question:
			return {
				renderIcon: (size: number) => <IconQuestionMark size={size} />,
				label: 'Questions',
			};
		case EntityType.test:
			return {
				renderIcon: (size: number) => <IconClipboardCheck size={size} />,
				label: 'Tests',
			};
		case EntityType.document:
			return {
				renderIcon: (size: number) => <IconFileDescription size={size} />,
				label: 'Documents',
			};
		case EntityType.table:
			return {
				renderIcon: (size: number) => <IconTable size={size} />,
				label: 'Tables',
			};
		case EntityType.dashboard:
			return {
				renderIcon: (size: number) => <IconLayoutDashboard size={size} />,
				label: 'Dashboards',
			};
		case EntityType.job:
			return {
				renderIcon: (size: number) => <IconCircleCaretRight size={size} />,
				label: 'Jobs',
			};
		case EntityType.collection:
			return {
				renderIcon: (size: number) => <IconFolders size={size} />,
				label: 'Collections',
			};
		case EntityType.column:
			return {
				renderIcon: (size: number) => <IconColumns size={size} />,
				label: 'Columns',
			};
		case EntityType.chart:
			return {
				renderIcon: (size: number) => <IconChartBar size={size} />,
				label: 'Charts',
			};
		case EntityType.event:
			return {
				renderIcon: (size: number) => <IconClick size={size} />,
				label: 'Events',
			};
		case EntityType.database:
			return {
				renderIcon: (size: number) => <IconDatabase size={size} />,
				label: 'Database',
			};
		case EntityType.schema:
			return {
				renderIcon: (size: number) => <IconSchema size={size} />,
				label: 'Schema',
			};
		case EntityType.dashboard_group:
			return {
				renderIcon: (size: number) => <IconFolder size={size} />,
				label: 'Dashboard Group',
			};
		case EntityType.job_group:
			return {
				renderIcon: (size: number) => <IconFolder size={size} />,
				label: 'Job Group',
			};
		case EntityType.template:
			return {
				renderIcon: (size: number) => <IconTemplate size={size} />,
				label: 'Templates',
			};
		case EntityType.bi_dashboard:
			return {
				renderIcon: (size: number) => (
					<Icon
						name={
							DEFAULT_ICONS[EntityType.bi_dashboard]?.iconName ??
							'customDashboard'
						}
						iconWidth={size}
						iconHeight={size}
						style={{ width: size, height: size }}
					/>
				),
				label: 'Dashboards',
			};
		case EntityType.bi_metric:
			return {
				renderIcon: (size: number) => (
					<Icon
						name={
							DEFAULT_ICONS[EntityType.bi_metric]?.iconName ?? 'chartHistogram'
						}
						iconWidth={size}
						iconHeight={size}
						style={{ width: size, height: size }}
					/>
				),
				label: 'Metrics',
			};
		default:
			return {
				renderIcon: (size: number) => <IconCloud size={size} />,
				label: 'Resource',
			};
	}
};

export const getEntityTypeIcon = (
	entityType: EntityType | undefined
): JSX.Element => {
	switch (entityType) {
		case EntityType.user:
			return <Icon name="userCircle" color="icon/primary/default" />;
		case EntityType.dictionary_term:
			return <Icon name="book" color="icon/primary/default" />;
		case EntityType.glossary:
			return <Icon name="book" color="icon/primary/default" />;
		case EntityType.metric:
			return <Icon name="chartHistogram" color="icon/primary/default" />;
		case EntityType.question:
			return <Icon name="questionMark" color="icon/primary/default" />;
		case EntityType.test:
			return <Icon name="fileCheck" color="icon/primary/default" />;
		case EntityType.document:
			return <Icon name="fileDescription" color="icon/primary/default" />;
		case EntityType.table:
			return <Icon name="table" color="icon/primary/default" />;
		case EntityType.dashboard:
			return <Icon name="customDashboard" color="icon/primary/default" />;
		case EntityType.job:
			return <Icon name="circleCaretRight" color="icon/primary/default" />;
		case EntityType.collection:
			return <Icon name="folders" color="icon/primary/default" />;
		case EntityType.column:
			return <Icon name="columns" color="icon/primary/default" />;
		case EntityType.chart:
			return <Icon name="chartBar" color="icon/primary/default" />;
		case EntityType.event:
			return <Icon name="click" color="icon/primary/default" />;
		case EntityType.database:
			return <Icon name="database" color="icon/primary/default" />;
		case EntityType.schema:
			return <Icon name="schema" color="icon/primary/default" />;
		case EntityType.dashboard_group:
			return <Icon name="folder" color="icon/primary/default" />;
		case EntityType.job_group:
			return <Icon name="folder" color="icon/primary/default" />;
		case EntityType.template:
			return <Icon name="template" color="icon/primary/default" />;
		default:
			return <Icon name="cloud" color="icon/primary/default" />;
	}
};
